<template>
  <div class="personalTimeTracking">
    <div class="grid">
      <Card>
        <template #title>
          {{ $t('time_tracking') }}
          <div class="personalTimeTracking" v-if="appointment && appointment.project">
            {{ $t('time_tracking') }} bei {{ appointment.project.customer.firstname }}
            {{ appointment.project.customer.lastname }}
          </div>
        </template>
        <template #content>
          <div v-if="canRecordTimeTracking" class="time-control-container">
            <div class="time-display" v-if="duration">
              {{ duration }}
            </div>
            <div class="time-display" v-else-if="startTime && timeTrackingState !== 'STOPPED'">
              <Spinner />
            </div>
            <div class="button-container">
              <Button
                v-if="timeTrackingState === 'STARTED'"
                class="time-action-button btn btn-danger"
                @click="stop"
              >
                <i
                  class="mr-2"
                  :class="timeTrackingType === 'DRIVING_TIME' ? 'fa fa-car' : 'fa fa-wrench'"
                ></i>
                <span>{{ $t(timeTrackingType || 'WORKING_TIME') }}</span>
                <i class="fa fa-stop ml-2"></i>
              </Button>
              <template v-else>
                <Button class="time-action-button btn btn-success" @click="start('WORKING_TIME')">
                  <i class="fa fa-wrench mr-2"></i>
                  <span>{{ $t('WORKING_TIME') }}</span>
                  <i class="fa fa-play ml-2"></i>
                </Button>
                <Button class="time-action-button btn btn-success" @click="start('DRIVING_TIME')">
                  <i class="fa fa-car mr-2"></i>
                  <span>{{ $t('DRIVING_TIME') }}</span>
                  <i class="fa fa-play ml-2"></i>
                </Button>
              </template>
            </div>
          </div>

          <Accordion v-if="hasMyTimeTrackingBlock" class="mt-3">
            <AccordionTab header="Erfasste Zeiten">
              <MyTimeTrackingTable />
            </AccordionTab>
          </Accordion>

          <Accordion class="mt-3">
            <AccordionTab header="Krankmeldung, SOKA und Urlaubsanträge">
              <MyEmployeeRequestTable />
            </AccordionTab>
          </Accordion>
        </template>
      </Card>
    </div>
    <Toast />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import MyTimeTrackingTable from '@/components/MyTimeTrackingTable';
import MyEmployeeRequestTable from '@/components/MyEmployeeRequestTable';
import Spinner from '@/components/Spinner.vue';
import Toast from 'primevue/toast';
import { coversAccessLevel } from '@/helpers/accessLevel';
import { DASHBOARD_MY_TIME_TRACKING_BLOCK } from '@/navigation/accessNodes';

export default {
  name: 'TimeTrackingCard',
  components: {
    Card,
    Button,
    Accordion,
    AccordionTab,
    MyTimeTrackingTable,
    MyEmployeeRequestTable,
    Spinner,
    Toast,
  },
  props: {
    project: { type: Object },
    appointment: { type: Object },
  },
  data() {
    return {
      startTime: null,
      stopTime: null,
      currentTime: null,
      timer: null,
      timeTrackingNumber: null,
      timeTrackingState: null,
      timeTrackingType: 'WORKING_TIME',
    };
  },

  computed: {
    ...mapGetters(['getTimeTrackings', 'getTimeTrackingByNumber', 'getAccessLevel']),
    ...mapGetters('auth', ['getCurrentUser']),

    /**
     * The time tracking part follows the block dashboard-my-time-tracking, the employee requests
     * do not (see feature page projekt-montage). With the switch off every level is editor.
     */
    hasMyTimeTrackingBlock() {
      return this.getAccessLevel(DASHBOARD_MY_TIME_TRACKING_BLOCK) !== null;
    },
    canRecordTimeTracking() {
      return coversAccessLevel(this.getAccessLevel(DASHBOARD_MY_TIME_TRACKING_BLOCK), 'editor');
    },

    duration() {
      if (
        !this.startTime ||
        !this.currentTime ||
        !this.timeTrackingState ||
        this.timeTrackingState !== 'STARTED'
      )
        return null;

      const diff = this.$dayjs(this.currentTime).diff(this.$dayjs(this.startTime), 'second');
      const hours = String(Math.floor(diff / 3600)).padStart(2, '0');
      const minutes = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
      const seconds = String(diff % 60).padStart(2, '0');
      // console.log('currentTime',this.startTime, this.currentTime);
      return `${hours}:${minutes}:${seconds}`;
    },

    latestTimeTracking() {
      return this.getTimeTrackings.find((tt) => tt.timeTrackingState === 'STARTED');
    },
  },

  methods: {
    ...mapActions(['startTimeTracking', 'stopTimeTracking', 'fetchTimeTrackingsByUser']),

    async loadTimeTrackingData() {
      try {
        if (!this.getCurrentUser || !this.getCurrentUser.id) {
          console.warn('Benutzer ist nicht verfügbar. Zeiterfassungsdaten werden nicht geladen.');
          return;
        }

        // console.log('loadTimeTrackingData', this.hasRunningTimeTracking());
        await this.fetchTimeTrackingsByUser({
          userId: this.getCurrentUser.id,
          page: 0,
          pageSize: 25,
          sortField: 'createdAt',
          sortOrder: -1,
        });

        if (this.hasRunningTimeTracking()) {
          console.log('hasRunningTimeTracking', this.latestTimeTracking);
          this.startTime = this.latestTimeTracking.startTime;
          this.currentTime = new Date().toISOString();
          this.timeTrackingState = this.latestTimeTracking.timeTrackingState;
          this.timeTrackingType = this.latestTimeTracking.timeTrackingType || 'WORKING_TIME';
          this.timeTrackingNumber = this.latestTimeTracking.number;
        }

        // console.log('getTimeTrackings', this.getTimeTrackings);
      } catch (error) {
        console.error('Fehler beim Laden der Zeitverfolgungsdaten:', error);
      }
    },

    hasRunningTimeTracking() {
      return this.getTimeTrackings.some((tt) => tt.timeTrackingState === 'STARTED');
    },

    startTimer() {
      this.timer = setInterval(() => {
        this.currentTime = new Date().toISOString();
      }, 1000);
    },

    stopTimer() {
      clearInterval(this.timer);
      this.timeTrackingState = 'STOPPED';
      this.startTime = null;
      this.currentTime = null;
      this.timeTrackingNumber = null;
      this.timeTrackingType = 'WORKING_TIME';
    },

    async start(timeTrackingType) {
      const now = new Date();
      now.setMilliseconds(0);

      try {
        const response = await this.startTimeTracking({
          project: this.project,
          appointment: this.appointment,
          timeTrackingType,
        });
        this.timeTrackingType = response?.timeTrackingType || timeTrackingType || 'WORKING_TIME';
        this.timeTrackingNumber = response?.number || null;
        this.$nextTick(() => {
          this.loadTimeTrackingData();
        });
      } catch (error) {
        console.error('Fehler beim Starten der Zeiterfassung:', error);
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ERROR'),
          detail: 'Fehler beim Starten der Zeiterfassung - Server nicht erreichbar!',
          life: 3000,
        });
      }
    },

    async stop() {
      try {
        const stopped = await this.stopTimeTracking();
        if (stopped) {
          clearInterval(this.timer);
          this.timeTrackingState = 'STOPPED';
          this.$nextTick(() => {
            this.loadTimeTrackingData();
          });
        }
      } catch (error) {
        console.error('Fehler beim Stoppen der Zeiterfassung:', error);
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ERROR'),
          detail: 'Fehler beim Stoppen der Zeiterfassung - Server nicht erreichbar!',
          life: 3000,
        });
      }
    },
  },
  async created() {},

  async mounted() {
    if (this.hasMyTimeTrackingBlock) {
      await this.loadTimeTrackingData();
    }
  },

  watch: {
    latestTimeTracking(newValue, oldValue) {
      // console.log('latestTimeTracking', newValue, oldValue);
      if (newValue) {
        this.startTime = newValue.startTime;
        this.timeTrackingState = newValue.timeTrackingState;
        this.timeTrackingType = newValue.timeTrackingType || 'WORKING_TIME';
        this.timeTrackingNumber = newValue.number;
        this.currentTime = new Date().toISOString();
      }
    },
    timeTrackingState(newValue, oldValue) {
      // console.log('timeTrackingState', newValue, oldValue);
      if (newValue === 'STARTED') {
        this.startTimer();
      } else if (newValue === 'STOPPED') {
        this.stopTimer();
      }

      if (!newValue && oldValue) {
        this.stopTimer();
      }
    },
  },
};
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
  gap: 0.5em;
}
.grid > div {
  padding: 1em;
  border: 2px;
}

.time-display {
  font-size: 2rem;
  margin: 0;
  margin-right: 1rem;
}

.mt-3 {
  margin-top: 1rem;
}
.section-title {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.time-control-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 1px;
  margin: 0.5rem 0;
}

.button-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.time-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3.1rem;
}

.btn-danger:hover {
  background-color: #c82333 !important;
  border-color: #bd2130 !important;
}

.circular-button:focus {
  outline: none !important;
  box-shadow: none !important;
}
</style>
